import { ActionTree } from 'vuex';
import { IVideoRecorderState } from '@/video/recorder/store/video-recorder.types';
import {
  EVideoRecorderAction,
  EVideoRecorderMutation,
  state as defaultState,
} from '@/video/recorder/store/video-recorder.constants';
import { RootState } from '@/store/store';
import { VideoService } from '@/api/video/services/video.service';
import { EVideoSource } from '@/video/types/enums/video-source';
import { IVideo } from '@/types/Video';
import { translate } from '@/helpers/translation.helper';

export const actions: ActionTree<IVideoRecorderState, RootState> = {
  [EVideoRecorderAction.SetStream]({ commit }, stream?: MediaStream) {
    commit(EVideoRecorderMutation.SetStream, stream);
  },
  [EVideoRecorderAction.CloseStream]({ commit, state }) {
    if (state.recorder.stream) {
      commit(EVideoRecorderMutation.CloseStream);
    }
  },
  [EVideoRecorderAction.SetError]({ commit }, error?: string) {
    commit(EVideoRecorderMutation.SetError, error);
  },
  async [EVideoRecorderAction.Save]({ commit, dispatch }, file: File) {
    commit(EVideoRecorderMutation.SetLoading, true);
    try {
      const {
        video,
        uploadLink,
      }: { video: IVideo; uploadLink: string } = await VideoService.createVideo({
        videoType: EVideoSource.Upload,
        videoTitle: file.name,
      });
      await dispatch(EVideoRecorderAction.SaveSuccess, {
        file,
        uploadLink,
        videoId: video.id,
      });
    } catch (e) {
      dispatch(EVideoRecorderAction.SaveFailed);
    }
  },
  async [EVideoRecorderAction.SaveSuccess](
    { commit, dispatch },
    { file, uploadLink, videoId }: { file: File; uploadLink: string; videoId: string }
  ) {
    try {
      await VideoService.uploadVideo({ file, uploadLink });
      await VideoService.uploadVideoDone(videoId);
      commit(EVideoRecorderMutation.SetCreatedVideoId, videoId);
      commit(EVideoRecorderMutation.SetLoading, false);
    } catch (e) {
      dispatch(EVideoRecorderAction.SaveFailed);
    }
  },
  [EVideoRecorderAction.SaveFailed]({ commit, dispatch }) {
    commit(EVideoRecorderMutation.SetLoading, false);
    dispatch('alert/showDangerAlert', translate('failed-to-upload-video'), {
      root: true,
    });
  },
  [EVideoRecorderAction.ClearData]({ commit }) {
    commit(EVideoRecorderMutation.ResetState, { ...defaultState });
  },
  [EVideoRecorderAction.StartRecord]({ commit, dispatch }) {
    commit(EVideoRecorderMutation.SetRecordStatus, true);
    dispatch(EVideoRecorderAction.ToggleTimer);
  },
  [EVideoRecorderAction.StopRecord]({ commit, dispatch, state }) {
    commit(EVideoRecorderMutation.SetRecordStatus, false);
    commit(EVideoRecorderMutation.SetElapsedSeconds, 0);
    dispatch(EVideoRecorderAction.CloseStream);
    const superBuffer: Blob = new Blob(state.recorder.recordedBlobs, { type: 'video/webm' });
    commit(EVideoRecorderMutation.SetSuperBuffer, superBuffer);
  },
  [EVideoRecorderAction.AddRecordedBlob]({ commit, state }, blob: Blob) {
    const blobs: Blob[] = [...state.recorder.recordedBlobs, blob];
    commit(EVideoRecorderMutation.SetRecordedBlobs, blobs);
  },
  [EVideoRecorderAction.ToggleTimer]({ dispatch, state }) {
    if (!state.recorder.isRecording) {
      return;
    }
    setTimeout(() => dispatch(EVideoRecorderAction.IncreaseDuration), 1000);
  },
  [EVideoRecorderAction.IncreaseDuration]({ dispatch, state, commit }) {
    commit(EVideoRecorderMutation.SetElapsedSeconds, state.elapsedSeconds + 1);
    dispatch(EVideoRecorderAction.ToggleTimer);
  },
  [EVideoRecorderAction.SetSuperBufferUrl]({ commit }, superBufferUrl: string) {
    commit(EVideoRecorderMutation.SetSuperBufferURL, superBufferUrl);
  },
};
